import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Badge, Card, ListGroup, Alert, Form, Table } from 'react-bootstrap';
import { FaShoppingCart, FaStar, FaCheck, FaTruck, FaShieldAlt, FaPlay } from 'react-icons/fa';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import useProductsStore from '../store/productsStore';
import useCartStore from '../store/cartStore';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';
import Breadcrumb from '../components/Breadcrumb';
import { getImageUrl } from '../utils/imageUrl';
import { toast } from 'react-toastify';
import { trackProductView, trackAddToCart, trackVideoPlay } from '../utils/analytics';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentProduct, fetchProductById, isLoading } = useProductsStore();
  const { addToCart, updateQuantity, items } = useCartStore();
  
  const [quantity, setQuantity] = useState(1);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    fetchProductById(id);
  }, [id, fetchProductById]);

  useEffect(() => {
    if (currentProduct) {
      // Track product view
      trackProductView(currentProduct);
      
      // Set initial selected price
      if (currentProduct.priceTiers && currentProduct.priceTiers.length > 0) {
        const sorted = [...currentProduct.priceTiers].sort((a, b) => a.quantity - b.quantity);
        setSelectedPrice(sorted[0]);
      }
    }
  }, [currentProduct]);

  if (isLoading) {
    return (
      <Container className="my-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  if (!currentProduct) {
    return (
      <Container className="my-5">
        <Alert variant="danger">Product not found</Alert>
      </Container>
    );
  }

  const product = currentProduct;
  const images = (product.images && product.images.length > 0 ? product.images : [product.image]).map(getImageUrl);
  
  const calculatePrice = () => {
    if (!product.priceTiers || product.priceTiers.length === 0) {
      return product.basePrice * quantity;
    }

    // Find applicable tier
    const sortedTiers = [...product.priceTiers].sort((a, b) => b.quantity - a.quantity);
    const applicableTier = sortedTiers.find(tier => quantity >= tier.quantity);
    
    if (applicableTier) {
      return applicableTier.pricePerUnit * quantity;
    }
    
    return product.basePrice * quantity;
  };

  const getPricePerUnit = () => {
    if (!product.priceTiers || product.priceTiers.length === 0) {
      return product.basePrice;
    }

    const sortedTiers = [...product.priceTiers].sort((a, b) => b.quantity - a.quantity);
    const applicableTier = sortedTiers.find(tier => quantity >= tier.quantity);
    
    return applicableTier ? applicableTier.pricePerUnit : product.basePrice;
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    trackAddToCart(product, quantity);
    toast.success(`Added ${quantity} item(s) to cart!`);
  };

  const handleBuyNow = () => {
    addToCart(product, quantity);
    navigate('/checkout');
  };

  const getDiscountPercentage = () => {
    if (product.originalPrice && product.originalPrice > product.basePrice) {
      return Math.round(((product.originalPrice - product.basePrice) / product.originalPrice) * 100);
    }
    return 0;
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : null;
  };

  const handleVideoPlay = () => {
    setShowVideo(true);
    trackVideoPlay(product.name, product.youtubeUrl);
  };

  const breadcrumbItems = [
    { label: 'Products', link: '/products' },
    { label: product.category, link: `/products?category=${encodeURIComponent(product.category)}` },
    { label: product.name },
  ];

  const discountPercent = getDiscountPercentage();
  const youtubeEmbedUrl = getYoutubeEmbedUrl(product.youtubeUrl);

  // SEO data
  const seoTitle = product.seo?.metaTitle || product.name;
  const seoDescription = product.seo?.metaDescription || product.shortDescription;
  const seoKeywords = product.seo?.keywords || [product.category, product.name, 'software', 'license'];

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={images[0]}
        url={`/product/${product._id}`}
        type="product"
        product={product}
      />
      <SchemaMarkup product={product} />

      <Container className="my-4">
        <Breadcrumb items={breadcrumbItems} />

        <Row>
          {/* Left Column - Images */}
          <Col lg={6} className="mb-4">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-2">
                <Carousel
                  showArrows={true}
                  showThumbs={images.length > 1}
                  showStatus={false}
                  infiniteLoop={true}
                  useKeyboardArrows={true}
                  dynamicHeight={false}
                  className="product-carousel"
                >
                  {images.map((img, index) => (
                    <div key={index} style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
                      <img 
                        src={img} 
                        alt={`${product.name} - ${index + 1}`}
                        style={{ maxHeight: '400px', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    </div>
                  ))}
                </Carousel>
              </Card.Body>
            </Card>

            {/* YouTube Video */}
            {youtubeEmbedUrl && (
              <Card className="mt-3 border-0 shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">
                    <FaPlay className="me-2 text-danger" />
                    Product Video
                  </h5>
                  {showVideo ? (
                    <div className="ratio ratio-16x9">
                      <iframe
                        src={youtubeEmbedUrl}
                        title={`${product.name} video`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <div 
                      className="ratio ratio-16x9 bg-dark d-flex align-items-center justify-content-center" 
                      style={{ cursor: 'pointer' }}
                      onClick={handleVideoPlay}
                    >
                      <Button variant="danger" size="lg">
                        <FaPlay className="me-2" />
                        Watch Video
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>

          {/* Right Column - Details */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body>
                {/* Badges */}
                <div className="mb-3">
                  {product.badges?.isNew && <Badge bg="success" className="me-2">New</Badge>}
                  {product.badges?.isPopular && <Badge bg="warning" className="me-2">Popular</Badge>}
                  {product.badges?.isBestSeller && <Badge bg="danger" className="me-2">Best Seller</Badge>}
                  <Badge bg="info">{product.category}</Badge>
                </div>

                {/* Title */}
                <h1 className="h2 mb-3">{product.name}</h1>

                {/* Rating - Placeholder */}
                <div className="mb-3">
                  <FaStar className="text-warning me-1" />
                  <FaStar className="text-warning me-1" />
                  <FaStar className="text-warning me-1" />
                  <FaStar className="text-warning me-1" />
                  <FaStar className="text-muted me-1" />
                  <span className="text-muted">(4.0 / 5)</span>
                </div>

                {/* Price */}
                <div className="mb-4">
                  {product.originalPrice && product.originalPrice > product.basePrice ? (
                    <div>
                      <h3 className="text-primary mb-0">
                        ₹{getPricePerUnit().toFixed(2)}
                        <span className="text-decoration-line-through text-muted h5 ms-3">
                          ₹{product.originalPrice}
                        </span>
                        {discountPercent > 0 && (
                          <Badge bg="danger" className="ms-3 fs-6">{discountPercent}% OFF</Badge>
                        )}
                      </h3>
                      <small className="text-muted">per license</small>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-primary mb-0">₹{getPricePerUnit().toFixed(2)}</h3>
                      <small className="text-muted">per license</small>
                    </div>
                  )}
                </div>

                {/* Stock Status */}
                <div className="mb-4">
                  {product.stockCount > 0 ? (
                    <Alert variant="success" className="py-2">
                      <FaCheck className="me-2" />
                      <strong>{product.stockCount}</strong> licenses available
                    </Alert>
                  ) : (
                    <Alert variant="danger" className="py-2">
                      Out of stock
                    </Alert>
                  )}
                </div>

                {/* Quantity and Pricing Tiers */}
                {product.priceTiers && product.priceTiers.length > 0 && (
                  <Card className="mb-4 bg-light border-0">
                    <Card.Body>
                      <h6 className="mb-3">Bulk Pricing Available</h6>
                      <Table bordered size="sm" className="mb-0 bg-white">
                        <thead>
                          <tr>
                            <th>Quantity</th>
                            <th>Price per Unit</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className={quantity < (product.priceTiers[0]?.quantity || 0) ? 'table-active' : ''}>
                            <td>1 - {(product.priceTiers[0]?.quantity || 1) - 1}</td>
                            <td>₹{product.basePrice}</td>
                            <td>₹{(product.basePrice * Math.min(quantity, (product.priceTiers[0]?.quantity || 1) - 1)).toFixed(2)}</td>
                          </tr>
                          {[...product.priceTiers].sort((a, b) => a.quantity - b.quantity).map((tier, index, arr) => {
                            const nextTier = arr[index + 1];
                            const isActive = quantity >= tier.quantity && (!nextTier || quantity < nextTier.quantity);
                            return (
                              <tr key={index} className={isActive ? 'table-primary' : ''}>
                                <td>
                                  {tier.quantity}{nextTier ? ` - ${nextTier.quantity - 1}` : '+'}
                                </td>
                                <td>₹{tier.pricePerUnit}</td>
                                <td>
                                  {isActive && `₹${(tier.pricePerUnit * quantity).toFixed(2)}`}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}

                {/* Quantity Selector */}
                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Quantity</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max={product.stockCount}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Math.min(product.stockCount, parseInt(e.target.value) || 1)))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Total Price</Form.Label>
                      <div className="display-6 text-primary">
                        ₹{calculatePrice().toFixed(2)}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Action Buttons */}
                <div className="d-grid gap-2 mb-3">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleBuyNow}
                    disabled={product.stockCount === 0}
                  >
                    Buy Now
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="lg"
                    onClick={handleAddToCart}
                    disabled={product.stockCount === 0}
                  >
                    <FaShoppingCart className="me-2" />
                    Add to Cart
                  </Button>
                </div>

                {/* Trust Badges */}
                <Row className="text-center text-muted small">
                  <Col xs={4}>
                    <FaTruck size={24} className="mb-2" />
                    <div>Instant Delivery</div>
                  </Col>
                  <Col xs={4}>
                    <FaShieldAlt size={24} className="mb-2" />
                    <div>100% Genuine</div>
                  </Col>
                  <Col xs={4}>
                    <FaCheck size={24} className="mb-2" />
                    <div>Verified</div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Product Description */}
        <Row className="mt-4">
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h3 className="mb-4">Product Description</h3>
                <p className="lead">{product.shortDescription}</p>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {product.description}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Delivery Information */}
        <Row className="mt-4">
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h4 className="mb-3">Delivery Information</h4>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <FaCheck className="text-success me-2" />
                    {product.deliveryPolicy}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <FaCheck className="text-success me-2" />
                    License key will be sent to your registered email
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <FaCheck className="text-success me-2" />
                    WhatsApp notification for order confirmation
                  </ListGroup.Item>
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ProductDetail;
